// ONE list for the admin's navigation: the desktop menu (grouped), the phone
// bottom tabs and the phone « More » sheet (owner spec 2026-10-01,
// docs/superpowers/plans/2026-10-01-app-feel.md › Navigation).
//
// ⚠️ AN ENTRY IS SHOWN ONLY WHEN ITS ROUTE WOULD LET THE PERSON IN. Until
// 2026-10-01 the menu used its own permission (whatsapp.manage, users.manage,
// settings.manage) for three pages whose routes are admin-only — staff saw
// « Assistant WhatsApp », « Utilisateurs », « Paramètres », tapped, and were
// bounced to « / ». The guard of each entry is now the ROUTE's guard, copied
// from App.tsx into ADMIN_ROUTE_GUARDS, and navModel.test.ts reads App.tsx to
// prove the two never drift. Pure: no React, no DOM.

export type NavGroupId = 'daily' | 'business' | 'catalogue' | 'system';

/** What a route's <ProtectedRoute adminOnly perm=…> asks for. No perm = admin only. */
export interface RouteGuard { perm?: string }

/** Who is looking: the profile role and the permission check (lib/permissionsStore). */
export interface Access {
  role: 'admin' | 'staff' | 'customer' | null | undefined;
  can: (perm: string) => boolean;
}

export interface AdminNavItem {
  to: string;
  /** French label = the i18n key (lib/i18n.tsx). */
  label: string;
  /** Shorter label under a bottom-tab icon, when the menu label is too long. */
  tabLabel?: string;
  group: NavGroupId;
  /** Active only on exactly this path (the dashboard is /admin, a prefix of all). */
  exact?: boolean;
}

export const NAV_GROUPS: ReadonlyArray<{ id: NavGroupId; label: string }> = [
  { id: 'daily', label: 'Au quotidien' },
  { id: 'business', label: 'Gestion' },
  { id: 'catalogue', label: 'Catalogue' },
  { id: 'system', label: 'Système' },
];

/**
 * The guards of App.tsx's admin routes, path for path. ⚠️ Change App.tsx and
 * this together (navModel.test.ts fails otherwise). /admin/scanner is built on
 * another branch; its route is expected to be guarded by orders.edit_status.
 */
export const ADMIN_ROUTE_GUARDS: Record<string, RouteGuard> = {
  '/admin': { perm: 'dashboard.view' },
  '/admin/rapports': { perm: 'dashboard.view' },
  '/admin/orders': { perm: 'orders.view' },
  '/admin/orders/:shopifyOrderId': { perm: 'orders.view' },
  '/admin/customers': { perm: 'customers.view' },
  '/admin/customers/:id': {},
  '/admin/covers': { perm: 'covers.view' },
  '/admin/layouts': { perm: 'templates.view' },
  '/admin/layouts/review': {},
  '/admin/delivery': { perm: 'delivery.manage' },
  '/admin/products': { perm: 'products.manage' },
  '/admin/coupons': { perm: 'coupons.manage' },
  '/admin/stock': { perm: 'stock.manage' },
  '/admin/accounting': { perm: 'accounting.view' },
  '/admin/impression': { perm: 'production.print_batches' },
  '/admin/audit': { perm: 'audit.view' },
  '/admin/users': {},
  '/admin/assistant': {},
  '/admin/notifications': { perm: 'whatsapp.manage' },
  '/admin/settings': {},
  '/admin/ai-compare': {},
  '/admin/scanner': { perm: 'orders.edit_status' },
};

/** Desktop order, group by group. */
export const ADMIN_NAV: readonly AdminNavItem[] = [
  { to: '/admin/assistant', label: 'WhatsApp', group: 'daily' },
  { to: '/admin/orders', label: 'Commandes', group: 'daily' },
  { to: '/admin/scanner', label: 'Scanner', group: 'daily' },
  { to: '/admin/impression', label: 'Impression', group: 'daily' },
  { to: '/admin/delivery', label: 'Livraison AMEEX', tabLabel: 'AMEEX', group: 'daily' },
  // The home (2026-10-21): 3 blocks; everything else moved to « Rapports ».
  { to: '/admin', label: 'Accueil', group: 'business', exact: true },
  { to: '/admin/rapports', label: 'Rapports', group: 'business' },
  { to: '/admin/customers', label: 'Clients', group: 'business' },
  { to: '/admin/accounting', label: 'Comptabilité', tabLabel: 'Compta', group: 'business' },
  { to: '/admin/products', label: 'Produits', group: 'catalogue' },
  { to: '/admin/coupons', label: 'Coupons', group: 'catalogue' },
  { to: '/admin/stock', label: 'Stock', group: 'catalogue' },
  { to: '/admin/covers', label: 'Couvertures', group: 'catalogue' },
  { to: '/admin/layouts', label: 'Layout Templates', tabLabel: 'Modèles', group: 'catalogue' },
  { to: '/admin/users', label: 'Utilisateurs', tabLabel: 'Équipe', group: 'system' },
  { to: '/admin/audit', label: 'Journal', group: 'system' },
  { to: '/admin/notifications', label: 'Notifications', tabLabel: 'Alertes', group: 'system' },
  { to: '/admin/settings', label: 'Paramètres', tabLabel: 'Réglages', group: 'system' },
];

/** Phone bottom bar: the first 5 of these the person may open become the tabs. */
export const PHONE_TAB_PRIORITY: readonly string[] = [
  '/admin/assistant', '/admin/orders', '/admin/scanner', '/admin/impression', '/admin/delivery',
  // …and when one of those drops out, the next permitted page moves up:
  '/admin', '/admin/rapports', '/admin/customers', '/admin/accounting',
  '/admin/products', '/admin/coupons', '/admin/stock', '/admin/covers', '/admin/layouts',
  '/admin/users', '/admin/audit', '/admin/notifications', '/admin/settings',
];

export const PHONE_TAB_COUNT = 5;

/** components/ProtectedRoute.tsx, as a pure rule. */
export function canEnter(guard: RouteGuard, access: Access): boolean {
  if (access.role === 'admin') return true;
  if (access.role !== 'staff') return false;
  return !!guard.perm && access.can(guard.perm);
}

export function visibleNav(access: Access): AdminNavItem[] {
  return ADMIN_NAV.filter((item) => {
    const guard = ADMIN_ROUTE_GUARDS[item.to];
    return !!guard && canEnter(guard, access);
  });
}

export function desktopGroups(access: Access): Array<{ group: NavGroupId; label: string; items: AdminNavItem[] }> {
  const visible = visibleNav(access);
  return NAV_GROUPS
    .map((g) => ({ group: g.id, label: g.label, items: visible.filter((i) => i.group === g.id) }))
    .filter((g) => g.items.length > 0);
}

export function phoneTabs(access: Access): AdminNavItem[] {
  const visible = new Map(visibleNav(access).map((i) => [i.to, i]));
  return PHONE_TAB_PRIORITY.flatMap((p) => visible.get(p) ?? []).slice(0, PHONE_TAB_COUNT);
}

export function moreGroups(access: Access): Array<{ group: NavGroupId; label: string; items: AdminNavItem[] }> {
  const tabs = new Set(phoneTabs(access).map((t) => t.to));
  return desktopGroups(access)
    .map((g) => ({ ...g, items: g.items.filter((i) => !tabs.has(i.to)) }))
    .filter((g) => g.items.length > 0);
}

/** Which entry is « you are here »: exact for the dashboard, else the longest section prefix. */
export function activeNavPath(pathname: string, items: readonly AdminNavItem[]): string | null {
  let best: string | null = null;
  for (const item of items) {
    const hit = item.exact
      ? pathname === item.to
      : pathname === item.to || pathname.startsWith(item.to + '/');
    if (hit && (!best || item.to.length > best.length)) best = item.to;
  }
  return best;
}

function matches(pattern: string, pathname: string): boolean {
  const a = pattern.split('/');
  const b = pathname.split('/');
  if (a.length !== b.length) return false;
  return a.every((seg, i) => seg.startsWith(':') ? b[i] !== '' : seg === b[i]);
}

/** The guard of the route that would render this path; a deeper unknown path
 *  falls back to its closest known parent (never to /admin itself). */
export function guardForPath(pathname: string): RouteGuard | null {
  for (const [pattern, guard] of Object.entries(ADMIN_ROUTE_GUARDS)) {
    if (matches(pattern, pathname)) return guard;
  }
  const segs = pathname.split('/');
  for (let n = segs.length - 1; n > 2; n--) {
    const parent = segs.slice(0, n).join('/');
    if (parent in ADMIN_ROUTE_GUARDS) return ADMIN_ROUTE_GUARDS[parent];
  }
  return null;
}
